/**
 * LinkOfferingToEventDialog
 * Dialog for linking an offering instance to an event instance.
 */

import { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useLinkOfferingToEvent, useEcosystemEvents } from '@/hooks/useEcosystemData';

interface LinkOfferingToEventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultEventInstanceId?: string;
}

export function LinkOfferingToEventDialog({
  open,
  onOpenChange,
  defaultEventInstanceId,
}: LinkOfferingToEventDialogProps) {
  const [eventInstanceId, setEventInstanceId] = useState('');
  const [offeringInstanceId, setOfferingInstanceId] = useState('');
  const [sessionCode, setSessionCode] = useState('');

  const linkOffering = useLinkOfferingToEvent();
  const { data: events } = useEcosystemEvents({ onlyActive: false });

  const { data: offerings } = useQuery({
    queryKey: ['all-offerings-for-linking'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('training_offering_instances')
        .select('id, start_datetime, end_datetime, delivery_type, city, state, provider_id')
        .order('start_datetime', { ascending: false })
        .limit(500);
      if (error) throw error;
      return data;
    },
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      setEventInstanceId(defaultEventInstanceId || '');
      setOfferingInstanceId('');
      setSessionCode('');
    }
  }, [open, defaultEventInstanceId]);

  const handleSubmit = async () => {
    if (!eventInstanceId || !offeringInstanceId) {
      toast.error('Event instance and offering are required');
      return;
    }

    try {
      await linkOffering.mutateAsync({
        data: {
          event_instance_id: eventInstanceId,
          offering_instance_id: offeringInstanceId,
          session_code: sessionCode.trim() || undefined,
        },
      });
      toast.success('Offering linked to event');
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error.message || 'Failed to link offering');
    }
  };

  const formatOffering = (o: any) => {
    const date = o.start_datetime ? new Date(o.start_datetime).toLocaleDateString() : 'No date';
    const loc = o.city ? `${o.city}${o.state ? `, ${o.state}` : ''}` : o.delivery_type || '';
    return `${date} — ${loc} (${o.id.substring(0, 8)})`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Link Offering to Event</DialogTitle>
          <DialogDescription>Connect a training offering to an event instance.</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>Event Instance *</Label>
            <Select value={eventInstanceId} onValueChange={setEventInstanceId}>
              <SelectTrigger><SelectValue placeholder="Select event" /></SelectTrigger>
              <SelectContent>
                {(events || []).map((e: any) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.series?.series_name || e.stable_key} {e.start_date ? `(${e.start_date})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label>Offering Instance *</Label>
            <Select value={offeringInstanceId} onValueChange={setOfferingInstanceId}>
              <SelectTrigger><SelectValue placeholder="Select offering" /></SelectTrigger>
              <SelectContent>
                {(offerings || []).map((o: any) => (
                  <SelectItem key={o.id} value={o.id}>{formatOffering(o)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="sessionCode">Session Code</Label>
            <Input id="sessionCode" value={sessionCode} onChange={(e) => setSessionCode(e.target.value)} placeholder="e.g., WS-201" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={linkOffering.isPending}>
            {linkOffering.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Link Offering
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
